<template>
  <div class="district" :class="[ modal.isActive ? '' : 'u-pointer-none' ]">
    <app-keypoint
      v-for="(keypoint, index) in data.sbsKeypoints"
      :key="index"
      :position="keypoint.position"
      :lookAt="{ x: cameraPos.x, y: cameraPos.y, z: cameraPos.z }"
      @click="onSelectKeypoint(index)"
      :isSelected="index === selectedIndex"
      :isVisible="keypointVisible"
    />

    <transition name="modal" :duration="600">
      <app-modal v-if="modal.isActive" :content="modal.content" @modal:close="onCloseModal"></app-modal>
    </transition>

    <app-districts-nav ref="districtTitle" @onPrev="$emit('onPrev')" @onNext="$emit('onNext')" :class="[ isSidePanel ? 'is-panel-active' : '' ]" />

    <transition name="t-translate__right--custom">
      <div
        @mouseenter="onMouseEnterPanel"
        @mouseleave="onMouseLeavePanel"
        class="info u-w3of8 u-bg-black u-full-height u-pointer-all u-w4of8--md u-w8of8--sm"
        v-if="isSidePanel"
      >

        <transition name="article" :duration="600">
          <app-article v-if="article.isActive" :content="article.content" @modal:open="onOpenModal" class="u-absolute u-full-width u-full-height"></app-article>
        </transition>

        <div class="info__top u-middle u-bg-grey u-pad-t-md" ref="top">
          <router-link v-if="$device.width < 780" :to="'/district/' + $route.params.slug" class="article__link-back u-marg-b-md t-text--xsmall u-inline-block u-marg-l-lg u-marg-l-md--sm">
            <img src="/images/arrow-back.svg" alt="" class="article__link-back__ico u-inline-block u-marg-r-sm">
            <span class="t-text--xsmall u-white u-inline-block u-uppercase">Back to district</span>
          </router-link>
          <p class="t-h1 u-white u-pad-x-md">BULDINGS</p>
          <core-drag :active-index="selectedIndex" class="u-pad-l-md">
            <ul class="info__top__list u-flex u-inline-block u-nowrap u-pad-t-md u-pad-b-lg u-left js-drag u-pad-b-md--sm">
              <li v-for="item in data.sbsKeypoints" class="info__top__list__item u-inline-block js-drag-item u-align-top u-pad-r-lg u-pad-r-md--sm">
                <router-link @click.native="$ga.send('Building', 'Click', 'Building navigation')" draggable="false" class="info__top__list__item__link u-relative u-inline-block" :to="'/district/' + $route.params.slug + '/' + item.slugg">
                  <span class="t-text--big u-white u-inline-block">{{item.titre}}</span>
                </router-link>
              </li>
            </ul>
          </core-drag>
        </div>

        <!-- SEARCH & FILTER -->
        <transition :duration="400">
          <div class="u-relative u-bg-black u-pad-t-md u-pad-x-md u-marg-b-md u-pad-x-sm--sm" ref="search">
            <div class="u-relative">
              <div class="info__search u-relative u-inline-block u-pad-r-md">
                <input type="text" name="search" placeholder="Looking for something specific ?" class="info__search__input t-input t-input--small u-pad-l-lg u-full-width" v-model="searchInputValue" @input="onSearch">
                <img src="/images/search.svg" alt="" class="info__search__ico u-absolute u-marg-l-sm">
              </div>
              <button class="info__btn__filter t-button--simple" @click="onToggleFilters" ref="tags">
                <img src="/images/filter.svg" alt="" class="info__btn__filter__ico u-inline-block u-marg-r-sm">
                <span class="u-white u-inline-block u-uppercase">Filter</span>
              </button>

              <transition name="filters" :duration="500">
                <app-filters
                  v-show="isFiltersActive"
                  ref="filter"
                  :tags="data.sbsSubjects"
                  @close="onToggleFilters"
                  @toggle:tag="onToggleTag"
                  @toggle:tagType="onToggleTagType"
                />
              </transition>
            </div>
          </div>
        </transition>

        <!-- CONTENT -->
        <div class="info__content u-relative u-pad-x-md u-pad-b-md u-overflow-y-scroll u-pad-x-sm--sm u-no-scroll-bar" ref="content" :class="[ isFiltersActive || article.isActive ? 'is-fade-out' : '' ]">
          <template v-if="cards.length">
            <transition-group tag="div" name="card" :duration="400">
              <app-card
                v-for="(card, index) in cards"
                v-if="isCardsVisible"
                :content="card"
                :index="index"
                class="u-marg-b-md"
                ref="card"
                :key="'card-'+index"
                @modal:open="onOpenModal"
                :style="{ transitionDelay: index / 20 + 's, ' + index / 20 + 's' }"
              />
            </transition-group>
          </template>
          <template v-else>
            <div class="t-text t-text--big u-white u-center u-marg-t-lg">
              {{ $t('district.noContent') }}
            </div>
          </template>

          <transition name="info__content__overlay" :duration="500">
            <div v-show="isFiltersActive" class="info__content__overlay u-wrapper-panel u-bg-black" @click="onOverlayContentClick"></div>
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { Vector2, Vector3 } from 'three'
import gsap from 'gsap'

import { webGL } from '@/webGL/WebGL'
import { VirtualScroll } from '@/utils/VirtualScroll'
import { CameraControl } from '@/utils/CameraControl'
import { mapper, clamp } from '@/utils/Maths'

import AppArticle from '@/components/common/AppArticle'
import AppCard from '@/components/common/AppCard'
import AppDistrictsNav from '@/components/common/AppDistrictsNav'
import AppFilters from '@/components/common/AppFilters'
import AppKeypoint from '@/components/AppKeypoint'
import AppModal from '@/components/common/AppModal'
import AppSliderItem from '@/components/AppSliderItem'
import AppTag from '@/components/common/AppTag'
import CoreDrag from '@/components/common/CoreDrag'

export default {
  components: {
    AppArticle,
    AppCard,
    AppDistrictsNav,
    AppFilters,
    AppKeypoint,
    AppModal,
    AppSliderItem,
    AppTag,
    CoreDrag
  },

  computed: {
    data () {
      return this.$store.getters['data/districtBySlug'](this.$route.params.slug)
    },
    isSidePanel () {
      return this.selectedIndex !== -1
    }
  },

  props: {
    cameraPos: {
      type: Object,
      default: () => {return new Vector3()}
    }
  },

  watch: {
    isSidePanel () {
      this.placeCamera()
    },
    $route (to, from) {
      if ( to.name.includes('keypoint') || to.name.includes('article') ) {
        this.selectedIndex = this.data.sbsKeypoints.findIndex(keypoint => { return keypoint.slugg === to.params.id })
        this.getCards()

        this.$nextTick(() =>
        {
          this.onResize()
        })
      }
      else if ( !from.name.includes('district') ) {
        // this.isCameraFree = false
        // this.keypointVisible = false
        // if (to.name.includes('district')) {
        //   this.placeCamera()
        //   this.showKeypoints()
        // }
      }
      else {
        this.selectedIndex = -1
      }

      if (to.name.includes('keypoint')) {
        this.$ga.send('Building', 'Click', this.data.sbsKeypoints[this.selectedIndex].slugg)
      }

      if(to.name.includes('district') && this.selectedIndex !== -1) {
        this.selectedIndex = -1
        // this.placeCamera()
        this.showKeypoints()
      }

      if(to.name.includes('district') && from.name.includes('district')) {
        this.isCameraFree = false
        this.keypointVisible = false
        this.placeCamera()
        this.showKeypoints()
      }

      if(to.name.includes('article'))
      {
        this.article.isActive = true
        this.getArticle()
      }
      else
      {
        this.article.isActive = false
      }

      if(this.isFiltersActive)
      {
        this.onToggleFilters()
      }

      if((to.name.includes('keypoint') && from.name.includes('keypoint')) || (to.name.includes('keypoint') && !this.isCardsVisible))
      {
        this.switchCards()
      }
    }
  },

  data () {
    return {
      selectedIndex: -1,
      keypointVisible: false,
      isCardsVisible: true,
      isFiltersActive: false,
      modal:
      {
        isActive: false,
        content: null
      },
      article:
      {
        isActive: false,
        content: null,
        index: 0
      },
      searchInputValue: '',
      cards: null
    }
  },

  created () {
    this._query = {
      tagsId: [],
      contentTypesId: [],
      searchString: ''
    }

    this.pos = new Vector2()
    this.currentPos = new Vector2()

    this.virtualScroll = new VirtualScroll()
    this.isCameraFree = false

    this.virtualScroll.onPrev(()=>{
      this.$router.push(this.$local('city'))
    })
  },

  mounted () {
    // this.cameraControl = new CameraControl(webGL.camera, this.$el)
    this.$root.$el.addEventListener(this.$device.pointermove, this.onMousemove)

    if ( this.$route.name.includes('keypoint') || this.$route.name.includes('article') ) {
      this.selectedIndex = this.data.sbsKeypoints.findIndex(keypoint => { return keypoint.slugg === this.$route.params.id })
      this.getCards()
    }

    if(this.$route.name.includes('article')) {
      this.article.isActive = true
      this.getArticle()
    }
    else {
      this.article.isActive = false
    }

    this.isCardsVisible = !this.$route.name.includes('article')

    this.$hub.on('showoff:selectRandom', () => {
      if (this.data.sbsKeypoints.length) {
        this.onSelectKeypoint(Math.floor(Math.random() * this.data.sbsKeypoints.length))
      }
    })

    this.placeCamera()
    this.showKeypoints()

    this.$nextTick(() => {
      this.onResize()
    })

    this.$hub.emit('onBoarding:step', {
      step: this.$store.getters['data/tutorials'][3],
      pad: { h: 10, v: 10 },
      isNextBtn: true
    })

    // pretend like you don't see this
    this.$hub.emit('onBoarding:step', {
      step: this.$store.getters['data/tutorials'][4],
      el: this.$refs.districtTitle.$el,
      pad: { h: 10, v: 10 },
      isNextBtn: true,
      callback: () => {
        this.onSelectKeypoint(Math.floor(Math.random() * this.data.sbsKeypoints.length))
        setTimeout(() => {
          this.$hub.emit('onBoarding:step', {
            step: this.$store.getters['data/tutorials'][5],
            pad: { h: 10, v: 10 },
            el: this.$refs.tags,
            isNextBtn: true,
            callback: () => {
              this.$hub.emit('onBoarding:step', {
                step: this.$store.getters['data/tutorials'][6],
                pad: { h: 10, v: 10 },
                isNextBtn: true,
                el: this.$refs.card[0].$el.querySelector('.card__header__btn-fav'),
                callback: () => {
                  this.$hub.emit('onBoarding:step', {
                    step: this.$store.getters['data/tutorials'][7],
                    pad: { h: 10, v: 10 },
                    isNextBtn: true,
                    el: this.$refs.top,
                    callback: () => {
                      this.$hub.emit('onBoarding:step', {
                        step: this.$store.getters['data/tutorials'][8],
                        pad: { h: 10, v: 10 },
                        height: 250,
                        isNextBtn: true,
                        el: document.querySelector('#intercom'),
                      })
                    }
                  })
                }
              })
            }
          })          
        }, 1000)
      }
    })
  },

  beforeDestroy () {
    this.virtualScroll.onDestroy()
    this.isCameraFree = false
    this.keypointVisible = false
    this.selectedIndex = -1
    this.$root.$el.removeEventListener(this.$device.pointermove, this.onMousemove)

    gsap.killTweensOf(webGL.camera.position, 'x,y,z')
    gsap.killTweensOf(webGL.camera.look, 'x,y,z')
  },

  methods: {
    placeCamera () {

      this.isCameraFree = false
      this.currentPos = new Vector2()

      if (!this.isSidePanel){
        let duration = mapper(webGL.camera.position.distanceTo(this.data.cameraPos), 50, 150, 2, 4)
        duration = clamp(duration, 2, 4)
        this.baseCameraPos = this.data.cameraPos.clone()
        this.baseLookPos = this.data.look.clone()

        gsap.to(webGL.camera.position, {
          x: this.data.cameraPos.x,
          y: this.data.cameraPos.y,
          z: this.data.cameraPos.z,
          duration: duration,
          ease: 'power4.inOut',
          onComplete: () => {
            this.isCameraFree = true
          }
        })

        gsap.to(webGL.camera.look, {
          x: this.data.look.x,
          y: this.data.look.y,
          z: this.data.look.z,
          duration: duration,
          ease: 'power4.inOut'
        })
      }
      else if(this.isSidePanel && !this.$device.isTablet) {
        this.baseCameraPos = this.data.cameraPosDistrict.clone()
        this.baseLookPos = this.data.lookDistrict.clone()

        gsap.to(webGL.camera.position, {
          x: this.data.cameraPosDistrict.x,
          y: this.data.cameraPosDistrict.y,
          z: this.data.cameraPosDistrict.z,
          duration: 1,
          ease: 'power4.inOut',
          onComplete: () => {
            this.isCameraFree = true
          }
        })

        gsap.to(webGL.camera.look, {
          x: this.data.lookDistrict.x,
          y: this.data.lookDistrict.y,
          z: this.data.lookDistrict.z,
          duration: 1,
          ease: 'power4.inOut'
        })
      }
    },
    onSelectKeypoint (index) {
      if (index === -1 ) {
        this.$router.push( this.$local('district') )
      }
      else {
        this.$router.push({
          ...this.$local('keypoint'),
          params: {
            slug: this.$route.params.slug,
            id: this.data.sbsKeypoints[index].slugg
          }
        })
      }
    },
    onMousemove (event) {
      if(this.modal.isActive) {
        return
      }

      let pos = this.$device.getPointerPosition(event)
      this.pos = new Vector2(( pos.x / this.$device.width ) * 2 - 1, - ( pos.y / this.$device.height ) * 2 + 1)
    },
    onMouseEnterPanel () {
      this.virtualScroll.active = false
    },
    onMouseLeavePanel () {
      // this.virtualScroll.active = true
    },
    onUpdate () {
      if (this.isCameraFree && this.data) {
        this.currentPos.x += (this.pos.x - this.currentPos.x) * .01
        this.currentPos.y += (this.pos.y - this.currentPos.y) * .01

        webGL.camera.position.x = this.baseCameraPos.x + this.currentPos.x * 5
        webGL.camera.position.y = this.baseCameraPos.y + this.currentPos.y * 5

        webGL.camera.forceUpdateLook = true
      }
    },
    showKeypoints () {
      setTimeout(() => {
        this.keypointVisible = true
      }, 2000)
    },
    onResize ()  {
      if(this.selectedIndex !== -1) {
        this.$refs.content.style.height = this.$device.height - (this.$refs.top.offsetHeight + this.$refs.search.offsetHeight) + 'px'
      }
    },
    onOpenModal (content) {
      this.modal.isActive = true
      this.modal.content = content
    },

    onCloseModal () {
      this.modal.isActive = false
    },

    switchCards(index) {
      clearTimeout(this._switchCardsTimer)
      this.isCardsVisible = false

      this._switchCardsTimer = setTimeout(() =>
      {
        this.isCardsVisible = true
      }, 500)
    },

    onToggleFilters() {
      this.isFiltersActive = !this.isFiltersActive
    },

    getArticle() {
      this.article.index = this.data.sbsKeypoints[this.selectedIndex].entries.findIndex(article =>
      {
        return article.slug === this.$route.params.articleSlug
      })

      this.article.content = this.data.sbsKeypoints[this.selectedIndex].entries[this.article.index]
    },

    onOverlayContentClick() {
      if(this.isFiltersActive)
      {
        this.onToggleFilters()
      }
    },

    onSearch() {
      clearTimeout(this._typeTimer)
      this._query.searchString = this.searchInputValue

      //wait for type break to send request
      this._typeTimer = setTimeout(() =>
      {
        this.onFilter()
      }, 400)
    },

    onToggleTag(isActive, tag) {
      const id = +tag.id

      if(isActive)
      {
        if(!this._query.tagsId.includes(id))
        {
          this._query.tagsId.push(id)
        }
      }
      else
      {
        if(this._query.tagsId.includes(id))
        {
          const removeIndex = this._query.tagsId.indexOf(id)
          this._query.tagsId.splice(removeIndex, 1)
        }
      }
      this.onFilter()
    },

    onToggleTagType(isActive, tag) {
      const id = tag.id

      if(isActive)
      {
        if(!this._query.contentTypesId.includes(id))
        {
          this._query.contentTypesId.push(id)
        }
      }
      else
      {
        if(this._query.contentTypesId.includes(id))
        {
          const removeIndex = this._query.contentTypesId.indexOf(id)
          this._query.contentTypesId.splice(removeIndex, 1)
        }
      }
      this.onFilter()
    },

    getCards() {
      if(this.selectedIndex !== -1) {
        this.switchCards()
        this.cards = this.data.sbsKeypoints[this.selectedIndex].entries
      }
    },

    async onFilter() {
      this.isCardsVisible = false
      this.$el.classList.add('is-waiting')
      await this.$store.dispatch('data/search', { search: this._query.searchString, subjects: this._query.tagsId, districtSlug: this.data.slug, types: this._query.contentTypesId })

        this.isCardsVisible = true
        this.$el.classList.remove('is-waiting')
        this.cards = this.$store.getters['data/searchedPosts']
    }
  }
}
</script>

<style lang="stylus">
@import '~@/styles/settings/variables'
@import '~@/styles/settings/easings'

.district
  // z-index 10
  
  &.is-waiting
    cursor wait

    a, button, input
      cursor wait
  
.info
  z-index 1
  position absolute
  top 0
  right 0
  
  &__content {
    transform translateZ(0)
    transition opacity .4s
  }
  
  &__content__overlay {
    opacity .5
    transform translateZ(0)
    transition opacity .4s
    
    &-enter,
    &-leave-to {
      opacity 0
    }
  }
  
  &__top__list__item
    white-space normal
    max-width 15vw
    
    @media ( max-width: $mq-breakpoints.tablet )
      max-width 33vw
      
    @media ( max-width: $mq-breakpoints.mobile )
      max-width 50vw
    
    &__link
      opacity .4
      transition opacity .3s linear

      &:after
        position absolute
        content ''
        
        bottom -15px
        left 0
        
        height 4px
        width 100%
        
        border-radius 5px
        
        background $gradients.green
        transform scaleX(0) translateZ(0)
        transform-origin 0 0
        transition transform .6s ease-out-quint
      
      &.router-link-active
        opacity 1

        &:after
          transform scaleX(1) translateZ(0)
  
  &__search {
    width calc(100% - 100px)
    &__ico {
      width 19px
      top 50%
      left 0
      transform translateY(-50%)
    }
    
    &__input {
      font-weight $weights.light
      font-size 12px
      
      &::-webkit-input-placeholder {
        font-size 12px
        font-weight $weights.light
        color $colors.grey-light
      }
    }
  }
  
  &__btn__filter__ico {
    width 22px
  }
  
  &__btn
    border solid 2px $colors.white
    border-radius 50%
    overflow hidden
    
    // max-width 52px
    
    &__svg
      padding 25%
      
      &--hover
        opacity 0
        transition opacity .3s linear
    
    &:hover &__svg--hover 
      opacity 1
      
  &__close
    position absolute
    top calc( 50% - 15px )
    right calc( 100% - 5px )
    width 30px
    border-radius 50% 0 0 50%
    overflow hidden

    &__svg
      padding 25% 0 25% 0
      
      &--hover
        opacity 0
        transition opacity .3s linear
    
    &:hover &__svg--hover 
      opacity 1
  
  &__bottom
    position absolute
    bottom 0
    left 0
    Width 100%
    height 100px
    
    &:before 
      content ''
      position absolute
      top 0
      left 0
      width 100%
      height 100%
      box-shadow: 0px -5px 20px 0px $colors.white
      opacity .1
      pointer-events none
    
    &__item
      cursor pointer

      &__svg
        &:after
          border-radius 2px
          position absolute
          top calc( 100% + 3px )
          left 0
          width 100%
          content ''
          height 3px
          background $colors.white

</style>